import { ImageBackground } from 'expo-image';
import { Image } from 'expo-image';
import * as ExpoLocation from 'expo-location';
import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import React from 'react';
import {
  ActivityIndicator,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import Modal from 'react-native-modal';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Checkin } from '@/api';
import { useCreateCheckin } from '@/api/checkin/use-create-checkin';
import { useUpdateCheckin } from '@/api/checkin/use-update-checkin';
import { useCustomer } from '@/api/customers/use-customer';
import { useReservation } from '@/api/reservations/use-reservation';
import { useReservationAcceptCarAsIs } from '@/api/reservations/use-reservation-accept-car-as-is';
import { findAdditionalDriver } from '@/components/additional-drivers/status';
import BackButton from '@/components/back-button';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { StepType } from '@/components/trip-timeline';
import { getDistanceInMiles, isDesktop } from '@/core';
import useCheckinStore from '@/stores/use-checkin';
import { Button, Card, Text } from '@/ui';
import { CheckItem } from '@/ui/check-item';
import { ConfirmModal } from '@/ui/confirm-modal';
import { Icon } from '@/ui/icon';
import { ArrowRight, Phone } from '@/ui/icons';
import { AppStore } from '@/ui/icons/app-store';
import { GooglePlay } from '@/ui/icons/google-play';
import { KeyCard } from '@/ui/icons/key-card';
import { Loader } from '@/ui/loader';

import Tutorial from './tutorial';

export default function Checkin() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const { id } = useLocalSearchParams();
  const [showTutorial, setShowTutorial] = React.useState(false);
  const [checkin, setCheckin] = React.useState<Checkin | null>(null);
  const setStep = useCheckinStore((state) => state.setStep);
  const [showCancelWarningModal, setShowCancelWarningModal] =
    React.useState(false);
  const [showAllowPreciseLocationModal, setShowAllowPreciseLocationModal] =
    React.useState(false);
  const [isFarFromCar, setIsFarFromCar] = React.useState(false);
  const [loadingLocation, setLoadingLocation] = React.useState(false);
  const [acceptedConditions, setAcceptedConditions] = React.useState(false);
  const [showWebLocationInpreciseError, setShowWebLocationInpreciseError] =
    React.useState(false);

  const {
    data: resData,
    isPending: isFetchingReservation,
    refetch,
  } = useReservation({
    //@ts-ignore
    variables: { id: id },
    enabled: !!id,
  });

  const { data: customer } = useCustomer();

  const {
    mutateAsync: createCheckin,
    isPending: isCreatingCheckin,
    isError,
  } = useCreateCheckin();

  const { mutateAsync: updateCheckin, isPending: isUpdatingCheckin } =
    useUpdateCheckin();

  const {
    mutateAsync: updateReservationAcceptCarAsIs,
    isPending: isAcceptingCarAsIs,
  } = useReservationAcceptCarAsIs();

  useFocusEffect(
    React.useCallback(() => {
      refetch();
    }, [refetch])
  );

  React.useEffect(() => {
    if (resData) {
      setAcceptedConditions(!!resData.acceptedCarConditionsAt);
    }
  }, [resData]);

  React.useEffect(() => {
    if (resData && resData.checkin) {
      setCheckin(resData.checkin);
    }
  }, [resData]);

  React.useEffect(() => {
    if (resData && !resData.car.isStandardFleetConnected) {
      setShowTutorial(!resData.checkin?.tutorialCompleted);
    }

    const createCheckinAsync = async () => {
      if (
        resData &&
        !resData.car.isStandardFleetConnected &&
        !resData.checkin
      ) {
        await handleCreateCheckin();
      }
    };

    createCheckinAsync();
  }, [resData]);

  const additionalDriver = React.useMemo(() => {
    if (!customer || !resData) return null;

    return findAdditionalDriver(resData?.additionalDrivers, customer);
  }, [resData, customer]);

  const licenseVerificationComplete = React.useMemo(() => {
    if (!checkin || !customer) return false;

    return checkin.licenseVerified;
  }, [checkin, customer]);

  const licenseVerificationStep = React.useMemo(() => {
    if (!customer) return '';

    if (!isDesktop() && checkin?.licenseVerified) {
      return StepType.COMPLETED;
    } else if (!isDesktop() && checkin?.tutorialCompleted) {
      return StepType.NEXT;
    } else {
      return StepType.UPCOMING;
    }
  }, [checkin, customer]);

  const acceptCarConditionsStep = React.useMemo(() => {
    if (!customer) return '';

    if (
      !isDesktop() &&
      licenseVerificationComplete &&
      resData?.acceptedCarConditionsAt !== null
    ) {
      return StepType.COMPLETED;
    } else if (!isDesktop() && licenseVerificationComplete) {
      return StepType.NEXT;
    } else {
      return StepType.UPCOMING;
    }
  }, [customer, licenseVerificationComplete, resData?.acceptedCarConditionsAt]);

  const handleCreateCheckin = async () => {
    try {
      const data = await createCheckin({ reservationId: parseInt(id) });
      setShowTutorial(true);
      setCheckin(data);
    } catch (e) {
      console.error('Error fetching data:', e);
    }
  };

  const handleTutorialComplete = async () => {
    if (checkin?.tutorialCompleted) {
      setShowTutorial(false);
      return;
    }

    try {
      const data = await updateCheckin({
        reservationId: parseInt(id),
        tutorialCompleted: true,
      });

      if (!resData.car.isStandardFleetConnected) {
        router.navigate(`/trips/${resData.id}/details`);
        return;
      }

      setShowTutorial(false);
      setCheckin(data);

      Toast.show({
        text1: 'Tutorial completed',
        type: 'success',
      });
    } catch (error: any) {
      console.error('Error fetching data:', error);
    }
  };

  const getCurrentLocation = async () => {
    setShowWebLocationInpreciseError(false);

    let { status } = await ExpoLocation.requestForegroundPermissionsAsync();

    if (status !== 'granted') {
      if (Platform.OS === 'web') {
        setShowWebLocationInpreciseError(true);
      } else {
        setShowAllowPreciseLocationModal(true);
      }

      return;
    }

    let location = await ExpoLocation.getCurrentPositionAsync({
      accuracy: ExpoLocation.LocationAccuracy.Highest,
    });

    if (location.coords.accuracy >= 100) {
      if (Platform.OS === 'web') {
        setShowWebLocationInpreciseError(true);
      } else {
        setShowAllowPreciseLocationModal(true);
      }

      return;
    }

    return {
      lat: location.coords.latitude,
      lng: location.coords.longitude,
    };
  };

  const handleStartVerification = async () => {
    try {
      setIsFarFromCar(false);

      setLoadingLocation(true);

      const { lat, lng } = await getCurrentLocation();

      setLoadingLocation(false);

      const distanceInMiles = getDistanceInMiles(
        { lat: lat, lon: lng },
        {
          lat: resData.car.carState.lat,
          lon: resData.car.carState.lon,
        }
      );

      if (distanceInMiles > 0.2) {
        setIsFarFromCar(true);
        return;
      }

      router.push(
        `/account/verify-license?trip_id=${resData.id}&reverify=true`
      );
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoadingLocation(false);
    }
  };

  const handleAcceptCarConditions = async () => {
    try {
      await updateReservationAcceptCarAsIs({
        id: parseInt(id),
      });

      refetch();
    } catch (e) {
      console.error('Error updating reservation car as is:', e);
    }
  };

  const stepColor = (type: StepType) => {
    switch (type) {
      case StepType.COMPLETED:
        return theme.colors.tripStep.completed.color;
      case StepType.NEXT:
        return theme.colors.tripStep.next.color;
      case StepType.UPCOMING:
        return theme.colors.tripStep.upcoming.color;
    }
  };

  const renderBody = () => {
    if (showTutorial) {
      return <Tutorial car={resData.car} onComplete={handleTutorialComplete} />;
    }

    return (
      <ScrollView
        style={{
          flexGrow: 1,
          padding: 16,
          minHeight: 1000,
        }}
        contentContainerStyle={{ gap: 24 }}
      >
        <Card>
          <View
            style={{
              borderBottomWidth: 1,
              borderColor: theme.colors.card.default.borderColor,
            }}
          >
            <View style={{ padding: 16, gap: 8 }}>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <View
                  style={{
                    backgroundColor: stepColor(
                      checkin?.tutorialCompleted
                        ? StepType.COMPLETED
                        : StepType.NEXT
                    ),
                    width: 8,
                    height: 8,
                    borderRadius: 8,
                    marginTop: 4,
                  }}
                />

                <Text
                  type="caption1"
                  style={{
                    color: stepColor(
                      checkin?.tutorialCompleted
                        ? StepType.COMPLETED
                        : StepType.NEXT
                    ),
                  }}
                >
                  {checkin?.tutorialCompleted ? 'Completed' : 'Next'}
                </Text>
              </View>

              <View>
                <Text type="bodyRegular">01: Tutorial</Text>

                <Text
                  type="bodyRegular"
                  style={{
                    color: theme.colors.text.secondary,
                  }}
                >
                  Learn the basics of driving a Tesla.
                </Text>
              </View>
            </View>
          </View>

          <View style={{ padding: 16 }}>
            <Button
              text="Go to tutorial"
              type="primaryExtraSmall"
              onPress={async () => {
                if (checkin) {
                  setShowTutorial(true);
                } else {
                  await handleCreateCheckin();
                }
              }}
              style={{ width: 120 }}
              disabled={checkin?.tutorialCompleted}
            />
          </View>
        </Card>

        {Platform.OS === 'web' ? (
          <View
            style={{
              borderWidth: 1,
              borderColor: theme.colors.card.default.borderColor,
              borderRadius: 16,
            }}
          >
            <View style={{ padding: 16, gap: 16 }}>
              <View style={{ alignItems: 'center' }}>
                <Text
                  type="bodyRegular"
                  style={{ textAlign: 'center', width: '80%' }}
                >
                  For the best experience download the Eon app
                </Text>
              </View>

              <View
                style={{
                  width: '100%',
                  flexDirection: 'row',
                  justifyContent: 'center',
                }}
              >
                {isDesktop() ? (
                  <Image
                    style={{
                      width: 100,
                      height: 100,
                    }}
                    source={
                      UnistylesRuntime.themeName === 'dark'
                        ? require('@/../public/images/qr-onelink-dark.png')
                        : require('@/../public/images/qr-onelink-light.png')
                    }
                    contentFit="cover"
                  />
                ) : (
                  <>
                    <Pressable
                      onPress={() => {
                        window.open(
                          'https://apps.apple.com/us/app/eon-instant-ev-rentals/id6448188911',
                          '_blank'
                        );
                      }}
                    >
                      <Icon
                        type="naked"
                        icon={<AppStore />}
                        width={160}
                        height={70}
                      />
                    </Pressable>

                    <Pressable
                      onPress={() => {
                        window.open(
                          'https://play.google.com/store/apps/details?id=com.eon.mobile',
                          '_blank'
                        );
                      }}
                    >
                      <Icon
                        type="naked"
                        icon={<GooglePlay />}
                        width={160}
                        height={70}
                      />
                    </Pressable>
                  </>
                )}
              </View>
            </View>
          </View>
        ) : null}

        {!isDesktop() ? (
          <>
            <Card
              style={{
                opacity: !isDesktop() && checkin?.tutorialCompleted ? 1 : 0.5,
              }}
            >
              <View
                style={{
                  ...(!isDesktop() &&
                    checkin?.tutorialCompleted && {
                      borderBottomWidth: 1,
                      borderColor: theme.colors.card.default.borderColor,
                    }),
                }}
              >
                <View style={{ padding: 16, gap: 8 }}>
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    <View
                      style={{
                        backgroundColor: stepColor(licenseVerificationStep),
                        width: 8,
                        height: 8,
                        borderRadius: 8,
                        marginTop: 4,
                      }}
                    />

                    <Text
                      type="caption1"
                      style={{
                        color: stepColor(licenseVerificationStep),
                      }}
                    >
                      {licenseVerificationComplete
                        ? 'Completed'
                        : 'Must be near the car'}
                    </Text>
                  </View>

                  <View style={{ gap: 4 }}>
                    <Text type="bodyRegular">02: Driver Verification</Text>

                    <Text type="caption1">
                      For safety we’ll verify it’s you at the car.{' '}
                    </Text>
                  </View>
                </View>
              </View>

              {!isDesktop() && checkin?.tutorialCompleted ? (
                <View>
                  <View style={{ padding: 16, gap: 16 }}>
                    <Button
                      leftIcon={loadingLocation ? <ActivityIndicator /> : null}
                      text={
                        loadingLocation
                          ? "Checking if you're near the car"
                          : 'Start Verification'
                      }
                      type="primaryExtraSmall"
                      onPress={handleStartVerification}
                      style={{ width: loadingLocation ? 250 : 130 }}
                      disabled={licenseVerificationComplete}
                    />

                    {showWebLocationInpreciseError ? (
                      <Text
                        type="caption1"
                        style={{ color: theme.colors.error }}
                      >
                        Allow precise location access to verify your identity or
                        download the Eon app.
                      </Text>
                    ) : null}

                    {isFarFromCar ? (
                      <Text
                        type="caption1"
                        style={{ color: theme.colors.error }}
                      >
                        You must be closer to the car to start verification.
                      </Text>
                    ) : null}
                  </View>
                </View>
              ) : null}
            </Card>

            {!additionalDriver ? (
              <Card
                style={{
                  opacity: checkin?.licenseVerified ? 1 : 0.5,
                }}
              >
                <View
                  style={{
                    ...(checkin?.licenseVerified && {
                      borderBottomWidth: 1,
                      borderColor: theme.colors.card.default.borderColor,
                    }),
                  }}
                >
                  <View style={{ padding: 16, gap: 8 }}>
                    <View style={{ flexDirection: 'row', gap: 8 }}>
                      <View
                        style={{
                          backgroundColor: stepColor(acceptCarConditionsStep),
                          width: 8,
                          height: 8,
                          borderRadius: 8,
                          marginTop: 4,
                        }}
                      />

                      <Text
                        type="caption1"
                        style={{
                          color: stepColor(acceptCarConditionsStep),
                        }}
                      >
                        {resData?.acceptedCarConditionsAt
                          ? 'Completed'
                          : licenseVerificationComplete
                          ? 'Review your car'
                          : 'Complete driver verification first'}
                      </Text>
                    </View>

                    <View style={{ gap: 4 }}>
                      <Text type="bodyRegular">03: Accept Car Conditions</Text>

                      <Text type="caption1">
                        Review and confirm the current condition of your car.
                        Report any issues to customer support immediately.
                      </Text>
                    </View>
                  </View>
                </View>

                {checkin?.licenseVerified ? (
                  <View>
                    <View style={{ padding: 16, gap: 16 }}>
                      <View>
                        <CheckItem
                          text="By unlocking this car, I confirm that I’ve reviewed the car condition and I am accepting it as-is."
                          caption="By checking this box, you agree this is your digital signature."
                          onPress={() => {
                            setAcceptedConditions(!acceptedConditions);
                          }}
                          active={acceptedConditions}
                          type="checkbox"
                          disabled={resData.acceptedCarConditionsAt !== null}
                        />
                      </View>

                      <View
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Button
                          text="Accept"
                          type="primaryExtraSmall"
                          onPress={handleAcceptCarConditions}
                          style={{ width: 80 }}
                          disabled={
                            !acceptedConditions ||
                            !!resData.acceptedCarConditionsAt
                          }
                        />

                        <Button
                          text="See what this means"
                          type="secondaryExtraSmall"
                          rightIcon={
                            <Icon
                              type="naked"
                              icon={<ArrowRight />}
                              fillColor={
                                theme.colors.button.secondary.default.text
                              }
                              width={10}
                              height={10}
                            />
                          }
                          onPress={async () => {
                            await WebBrowser.openBrowserAsync(
                              'https://www.eonrides.com/legal/your-rights-before-pickup'
                            );
                          }}
                          style={{ width: 160 }}
                        />
                      </View>
                    </View>
                  </View>
                ) : null}
              </Card>
            ) : null}

            <View
              style={{
                borderWidth: 1,
                borderColor: theme.colors.card.default.borderColor,
                borderRadius: 16,
                alignItems: 'center',
              }}
            />

            <View
              style={{
                gap: 16,
              }}
            >
              <View
                style={{
                  gap: 8,
                }}
              >
                <View style={{ gap: 4 }}>
                  <Text type="title2">Activate Your Car Key</Text>

                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Download and connect your digital key. You’ll need this to
                    drive the car. You can activate your key after completed the
                    check in
                  </Text>
                </View>
              </View>

              <View
                style={{
                  gap: 8,
                  alignItems: 'center',
                  flexDirection: 'row',
                }}
              >
                <View
                  style={{
                    padding: 8,
                    gap: 16,
                    borderWidth: 1,
                    borderColor: theme.colors.card.hover.borderColor,
                    borderRadius: 16,
                    alignItems: 'center',
                    width: '49%',
                  }}
                >
                  <View>
                    <Text
                      type="caption1"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      Step 1
                    </Text>
                  </View>

                  <View
                    style={{
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <Icon
                      type="dressed"
                      icon={<Phone />}
                      fillColor={theme.colors.icon.color}
                      width={24}
                      height={24}
                    />

                    <Text type="bodyRegular" style={{ textAlign: 'center' }}>
                      Download ChargeKey App
                    </Text>
                  </View>

                  <Button
                    text="Get the app"
                    type="primaryExtraSmall"
                    onPress={async () => {
                      if (Platform.OS === 'ios') {
                        await Linking.openURL(
                          'https://apps.apple.com/us/app/charge-key/id6496853294'
                        );
                      } else {
                        await Linking.openURL(
                          'https://play.google.com/store/apps/details?id=app.chargekey.android&hl=en_US'
                        );
                      }
                    }}
                  />
                </View>

                <View
                  style={{
                    padding: 8,
                    gap: 16,
                    borderWidth: 1,
                    borderColor: theme.colors.card.hover.borderColor,
                    borderRadius: 16,
                    alignItems: 'center',
                    width: '49%',
                  }}
                >
                  <View>
                    <Text
                      type="caption1"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      Step 2
                    </Text>
                  </View>

                  <View style={{ alignItems: 'center', gap: 8 }}>
                    <Icon
                      type="dressed"
                      icon={<KeyCard />}
                      fillColor={theme.colors.icon.color}
                      width={24}
                      height={24}
                    />

                    <Text type="bodyRegular" style={{ textAlign: 'center' }}>
                      Connect ChargeKey to Eon
                    </Text>
                  </View>

                  <Button
                    text="Connect to Eon"
                    type="primaryExtraSmall"
                    onPress={async () => {
                      router.replace(`/trips/${resData.id}/details`);

                      const sfKeyLink =
                        Platform.OS === 'web'
                          ? resData.sfCarWebKeyLink
                          : resData.sfCarKeyLink;

                      await Linking.openURL(sfKeyLink);
                    }}
                    style={{ width: 120 }}
                    disabled={!checkin?.isComplete}
                  />
                </View>
              </View>
            </View>
          </>
        ) : null}

        <View style={{ flexDirection: 'row' }}>
          <Pressable
            onPress={() => {
              Linking.openURL('tel:+18555987090');
            }}
            style={{
              flex: 1,
            }}
          >
            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                flexWrap: 'wrap',
                flex: 1,
              }}
            >
              Encountering an issue? Contact support at +18555987090
            </Text>
          </Pressable>
        </View>
      </ScrollView>
    );
  };

  if (
    isFetchingReservation ||
    isCreatingCheckin ||
    isUpdatingCheckin ||
    isAcceptingCarAsIs
  ) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 200, height: 200 }} />
      </View>
    );
  }

  return (
    <ScrollView
      contentContainerStyle={{
        flexGrow: 1,
        gap: 32,
        alignItems: 'center',
      }}
      style={{
        backgroundColor: theme.colors.background_1,
      }}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.container}>
        {Platform.OS === 'web' &&
        ['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <Stack.Screen
            options={{
              headerShown: false,
            }}
          />
        ) : (
          <Stack.Screen
            options={{
              title: showTutorial ? 'Tutorial' : 'Check-In',
              headerTitle: showTutorial ? 'Tutorial' : 'Check-In',
              // eslint-disable-next-line react/no-unstable-nested-components
              headerLeft: () => (
                <BackButton
                  onPress={() => {
                    if (!resData?.car.isStandardFleetConnected) {
                      setShowCancelWarningModal(true);
                      return;
                    }

                    if (isDesktop() && checkin?.tutorialCompleted) {
                      router.back();
                      return;
                    }

                    if (showTutorial) {
                      setShowTutorial(false);
                      return;
                    }

                    if (checkin?.isComplete) {
                      router.back();
                      return;
                    }

                    setShowCancelWarningModal(true);
                  }}
                />
              ),
              headerShown: true,
              headerStyle: {
                backgroundColor: theme.colors.background_1,
              },
              headerTintColor: theme.colors.text.primary,
              headerShadowVisible: false,
            }}
          />
        )}

        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <View style={{ gap: 16, padding: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                if (!resData?.car.isStandardFleetConnected) {
                  setShowCancelWarningModal(true);
                  return;
                }

                if (isDesktop() && checkin?.tutorialCompleted) {
                  router.back();
                  return;
                }

                if (checkin?.isComplete) {
                  router.back();
                  return;
                }

                if (!showTutorial) {
                  setShowCancelWarningModal(true);
                  return;
                } else {
                  setShowTutorial(false);
                  setStep(0);
                }
              }}
            />

            <Text type="largeTitle">
              {showTutorial ? 'Tutorial' : 'Check-In'}
            </Text>
          </View>
        ) : null}

        {renderBody()}

        <ConfirmModal
          isVisible={showCancelWarningModal}
          titleTx="checkin.cancelWarningTitle"
          bodyTx="checkin.cancelWarningBody"
          bodyTextStyle={{ textAlign: 'center' }}
          leftButton={{
            tx: 'checkin.cancelWarningCancelButton',
            onPress: () => {
              setShowTutorial(false);
              setStep(0);

              if (Platform.OS === 'android' && router.canDismiss()) {
                router.dismiss();
              }
              router.replace(`/trips/${resData.id}/details`);

              setShowCancelWarningModal(false);
            },
          }}
          rightButton={{
            tx: 'checkin.cancelWarningContinueButton',
            onPress: () => {
              setShowCancelWarningModal(false);
            },
          }}
          onDismiss={() => {
            setShowCancelWarningModal(false);
          }}
        />
      </View>

      <Modal
        animationIn="slideInUp"
        animationInTiming={300}
        animationOut="slideOutDown"
        animationOutTiming={300}
        isVisible={showAllowPreciseLocationModal}
        hasBackdrop
        style={{
          ...styles.showAllowPreciseLocationModal,
        }}
        onBackdropPress={() => {
          setShowAllowPreciseLocationModal(false);
        }}
      >
        <View
          style={{
            backgroundColor: theme.colors.background_2,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            gap: 8,
            borderRadius: 16,
            paddingBottom: 16,
          }}
        >
          <ImageBackground
            source={
              UnistylesRuntime.themeName === 'dark'
                ? require('../../../../../../public/images/trip-details/allow-location-dark.png')
                : require('../../../../../../public/images/trip-details/allow-location-light.png')
            }
            contentFit="cover"
            style={{
              flexDirection: 'row',
              justifyContent: 'center',
              paddingTop: 16,
              height: 200,
            }}
          >
            <NavigationButton
              type={NavigationButtonTypes.cancel}
              onPress={() => {
                setShowAllowPreciseLocationModal(false);
              }}
              style={{
                position: 'absolute',
                right: 16,
                top: 16,
                backgroundColor: theme.colors.navigationButton.fill,
                borderRadius: 100,
                padding: 4,
              }}
            />
          </ImageBackground>

          <View
            style={{
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.card.default.borderColor,
            }}
          >
            <View
              style={{
                padding: 16,
                paddingTop: 0,
                gap: 8,
              }}
            >
              <View style={{ gap: 8 }}>
                <Text type="title2">Allow Precise Location</Text>

                <View style={{ gap: 8 }}>
                  <Text type="bodyRegular">
                    To verify you’re near the car, we need access to your
                    precise location.
                  </Text>

                  <Text type="bodyRegular">
                    This helps ensure a secure handoff and enables key features
                    like unlocking and starting the car.
                  </Text>
                </View>
              </View>
            </View>
          </View>

          <View style={{ gap: 4, padding: 16 }}>
            <Button
              text="Go To Settings"
              type="primary"
              onPress={async () => {
                if (Platform.OS === 'ios') {
                  await Linking.openURL('app-settings:');
                } else {
                  // This will open your specific app's settings page on Android
                  await Linking.openSettings();
                }
              }}
            />
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    justifyContent: 'space-between',
    gap: 16,
    width: {
      xs: '100%',
      md: 600,
    },
    height: {
      md: 800,
    },
  },
  showAllowPreciseLocationModal: {
    margin: 0,
    alignItems: 'center',
    justifyContent: {
      xs: 'flex-end',
      md: 'center',
    },
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 800,
    },
    width: {
      xs: '100%',
      md: 700,
    },
  },
}));
